/**
 * <s-category-nav> — in-place category switching for the collection sidebar.
 *
 * Clicking an a[data-category-link] fetches that collection page and swaps, in place:
 *   [data-collection-hero]               the title banner
 *   s-live-form[data-collection-id]      toolbar, sidebar (filters, categories, tags) and grid
 * then pushes the collection URL so reloads, sharing and filters keep working for it.
 * Back/forward reloads the page for that URL. Modified clicks (new tab etc.) are left alone,
 * and any failure falls back to normal navigation.
 */

// Brings the collection toolbar and first product row into view after a sidebar action,
// only when the buyer has scrolled past them; respects the sticky header's height.
function scrollToCollectionResults() {
  const target = document.querySelector('[data-collection-layout]');
  if (!target) return;
  const header = document.querySelector('header');
  const headerOffset = header && getComputedStyle(header).position === 'sticky' ? header.getBoundingClientRect().height : 0;
  const top = target.getBoundingClientRect().top - headerOffset - 16;
  if (top >= 0) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollBy({ top, behavior: reduce ? 'auto' : 'smooth' });
}
let popstateBound = false;

class CategoryNav extends HTMLElement {
  #abort = null;

  connectedCallback() {
    this.#abort = new AbortController();
    this.addEventListener('click', (event) => this.#onClick(event), { signal: this.#abort.signal });
    if (!popstateBound) {
      popstateBound = true;
      window.addEventListener('popstate', (event) => {
        if (event.state?.categoryNav) window.location.reload();
      });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  async #onClick(event) {
    const link = event.target.closest('a[data-category-link]');
    if (!link || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    event.preventDefault();

    const currentForm = document.querySelector('s-live-form[data-collection-id]');
    const currentHero = document.querySelector('[data-collection-hero]');
    if (!currentForm) {
      window.location.assign(url);
      return;
    }

    currentForm.setAttribute('aria-busy', 'true');
    currentForm.style.opacity = '0.5';
    currentForm.style.transition = 'opacity 0.15s ease';

    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Category request failed: ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextForm = doc.querySelector('s-live-form[data-collection-id]');
      const nextHero = doc.querySelector('[data-collection-hero]');
      if (!nextForm) throw new Error('Collection markup not found');

      if (!history.state?.categoryNav) history.replaceState({ categoryNav: true }, '', window.location.href);
      history.pushState({ categoryNav: true }, '', url);
      if (doc.title) document.title = doc.title;

      if (currentHero && nextHero) currentHero.replaceWith(document.importNode(nextHero, true));
      const importedForm = document.importNode(nextForm, true);
      importedForm.style.opacity = '0';
      importedForm.style.transition = 'opacity 0.2s ease';
      currentForm.replaceWith(importedForm);
      requestAnimationFrame(() => {
        importedForm.style.opacity = '';
      });

      scrollToCollectionResults();
      importedForm.querySelector('a[data-category-link][aria-current="page"]')?.focus({ preventScroll: true });
    } catch {
      window.location.assign(url);
    }
  }
}

if (!customElements.get('s-category-nav')) customElements.define('s-category-nav', CategoryNav);
